import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CourseData,
  CourseMaterial,
  useCoursesLoaderData,
  useProgramDetails,
} from "./coursesLoader.ts";
import useToasts from "../../../root/core-ui/toast/useToasts.ts";
import { ProgramDecision } from "../../../../common/api/decimal/methods/course.ts";
import ProgramChangeSuccessToast from "./ProgramSwitcher/ProgramChangeSuccessToast.tsx";
import { useUpdateProgramChoiceFetcher } from "../actions/updateProgramChoiceAction.ts";
import DefaultErrorToast from "../common/DefaultErrorToast.tsx";
import { useLayoutData } from "../layout/myCoursesLayoutLoader.ts";
import PrintStatusChangeToast from "./PrintStatusChangeToast.tsx";

function countMaterials(
  courseData: CourseData,
  statusToCount: CourseMaterial["materialStatus"] = "opted_in"
) {
  return courseData.courses
    .map((course) => course.courseMaterials)
    .flat()
    .reduce((total, material) => {
      if (material) {
        return material.materialStatus === statusToCount ? total + 1 : total;
      }
      return total;
    }, 0);
}

interface PendingProgramChange {
  expectedProgram: ProgramDecision;
  oldCourseData: CourseData;
  oldProgramName: string;
  oldEffectiveProgram: ProgramDecision | null;
}

export default function useSwitchProgram(onDone?: () => void) {
  const { showToast } = useToasts();
  const courseData = useCoursesLoaderData();
  const programDetails = useProgramDetails();
  const { config } = useLayoutData();
  const fetcher = useUpdateProgramChoiceFetcher();

  const [pendingChange, setPendingChange] =
    useState<PendingProgramChange | null>(null);

  const announcePrintUpdates = useCallback(
    (courseData: CourseData) => {
      courseData.courses
        .map((course) => course.courseMaterials)
        .flat()
        .forEach((courseMaterial) => {
          if (courseMaterial && courseMaterial.materialFormat === "print") {
            showToast(
              <PrintStatusChangeToast
                termId={courseData.currentTerm!.id}
                eaProgram={programDetails.ea.name}
                programExcludesPrint={!programDetails.allowsPrint}
                vbid={courseMaterial.vbid}
                printStatus={courseMaterial.printStatus}
                courseMaterialId={courseMaterial.id}
              />
            );
          }
        });
    },
    [showToast, programDetails.ea.name, programDetails.allowsPrint]
  );

  const changeProgram = useCallback(
    (programDecision: ProgramDecision) => {
      if (!courseData.currentTerm!.id || !programDecision) {
        showToast(<DefaultErrorToast />);
        return;
      }

      setPendingChange({
        expectedProgram: programDecision,
        oldCourseData: courseData,
        oldProgramName: programDetails.name,
        oldEffectiveProgram: programDetails.userEffectiveProgram,
      });

      fetcher
        .submit(
          {
            programDecision,
            catalogId: courseData.currentTerm!.id,
          },
          {
            action: `/my-courses/program-decision`,
            method: "PUT",
          }
        )
        .then((result) => {
          if (!result.data?.ok) {
            setPendingChange(null);
            showToast(<DefaultErrorToast />);
            onDone?.();
          }
        })
        .catch(() => {
          setPendingChange(null);
          showToast(<DefaultErrorToast />);
          onDone?.();
        });
    },
    [courseData, fetcher, showToast, onDone, programDetails]
  );

  // Fires the success toast once revalidated data reflects the expected program.
  // May land at a different time than the fetcher completion.
  useEffect(() => {
    if (!pendingChange) {
      return;
    }
    if (programDetails.userEffectiveProgram !== pendingChange.expectedProgram) {
      return;
    }

    const { oldCourseData, oldProgramName, oldEffectiveProgram } =
      pendingChange;
    const { scope, participating, userEffectiveProgram, name } = programDetails;

    function getMaterialCount() {
      // In EA Only always count all opt-able materials
      if (scope === "ea_only") {
        if (!participating) {
          return countMaterials(courseData, "opted_out");
        }
        return countMaterials(courseData);
      }
      // When downgrading, don't count stuff you may already be opted of
      if (!participating) {
        return countMaterials(oldCourseData);
      }
      // When upgrading from nothing, look at the total amount of stuff
      // the new program contains
      if (oldEffectiveProgram === "no_program") {
        return countMaterials(courseData);
      }
      // Unsupported: When in FA IA/EA flips don't show counts
      return -1;
    }

    showToast(
      <ProgramChangeSuccessToast
        programScope={scope}
        newProgramName={name}
        oldProgramName={oldProgramName}
        oldProgram={oldEffectiveProgram!}
        newProgram={userEffectiveProgram}
        materialCount={getMaterialCount()}
      />
    );

    if (
      config.assets.showPrintPickup &&
      oldEffectiveProgram === "equitable_access"
    ) {
      announcePrintUpdates(courseData);
    }

    setPendingChange(null);
    onDone?.();
  }, [
    showToast,
    onDone,
    fetcher,
    courseData,
    config,
    announcePrintUpdates,
    pendingChange,
    programDetails,
  ]);

  const isProgramChangeProcessing = pendingChange !== null;

  return useMemo(
    () => ({
      isProgramChangeProcessing,
      changeProgram,
    }),
    [isProgramChangeProcessing, changeProgram]
  );
}
